import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Import a learning icon (using a book emoji as placeholder, you can replace with an actual image)
// import learningIcon from '../assets/learning_icon.png';

// Styled components
const LearningIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '180px',
  right: '20px',
  zIndex: showTooltip ? 9990 : 9998, // Lower z-index when tooltip is shown
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const LearningIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #26a69a 0%, #00897b 100%)', // Teal gradient
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(38, 166, 154, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(38, 166, 154, 0.5)',
  },
}));

const LearningTooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '10px',
  right: '80px',
  width: '280px',
  background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)', // Teal gradient
  border: '2px solid #26a69a',
  borderRadius: '12px',
  padding: theme.spacing(2),
  zIndex: 10012,
  boxShadow: '0 12px 40px rgba(38, 166, 154, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '20px',
    right: '-8px',
    width: 0,
    height: 0,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: '8px solid #00897b', // Teal arrow pointing right
  },
  '@keyframes tooltipSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px) scale(0.95)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
}));

const LearningIcon = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showHoverTooltip, setShowHoverTooltip] = useState(false);

  const handleIconClick = () => {
    setShowTooltip(!showTooltip);
  };

  const handleLearnNow = () => {
    window.open('http://cyberlagebild.de/learning', '_blank');
    setShowTooltip(false);
  };

  const handleLater = () => {
    setShowTooltip(false);
  };

  return (
    <LearningIconContainer showTooltip={showTooltip || showHoverTooltip}>
      {/* Learning Icon */}
      <LearningIconOverlay
        onClick={handleIconClick}
        onMouseEnter={() => setShowHoverTooltip(true)}
        onMouseLeave={() => setShowHoverTooltip(false)}
        title="Cyberlagebild Learning"
      >
        {/* Using emoji as placeholder - replace with actual image */}
        <Typography
          sx={{
            fontSize: '28px',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
          }}
        >
          📚
        </Typography>
        {/* Uncomment when you have an actual image:
        <img
          src={learningIcon}
          alt="Learning Resources"
          style={{
            width: '60px',
            height: '60px',
            objectFit: 'contain',
          }}
        />
        */}
      </LearningIconOverlay>
      
      {showHoverTooltip && (
        <LearningTooltip>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
            Learning Center
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Erweitere dein Wissen mit Lerninhalten
          </Typography>
        </LearningTooltip>
      )}

      {/* Learning Tooltip */}
      {showTooltip && (
        <LearningTooltip>
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            Cyberlagebild Learning Center - Aus Neugier wird Know-how.
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: '#e0f2f1',
              marginBottom: 2,
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            Erweitere dein Wissen mit unseren Lerninhalten
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: '#b2dfdb',
              marginBottom: 2,
              textAlign: 'center',
              fontSize: '0.85rem',
            }}
          >
            Kompakt erklärt. Direkt anwendbar.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleLearnNow}
              sx={{
                background: 'linear-gradient(135deg, #26a69a 0%, #00897b 100%)',
                color: '#ffffff',
                fontWeight: 'bold',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Jetzt lernen
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              onClick={handleLater}
              sx={{
                color: '#ffffff',
                borderColor: '#ffffff',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#e0f2f1',
                },
              }}
            >
              Später
            </Button>
          </Box>
        </LearningTooltip>
      )}
    </LearningIconContainer>
  );
};

export default LearningIcon;
